/**
 * Core utils to measure and report latency. Do not use them on their own,
 * these are building blocks to build a reporter with agnostic performance
 * storage and reporting engines.
 *
 * @see ./client.ts for more information on how to implement these.
 */
import {
  ExtraData,
  LatencyOptions,
  PerformanceStorage,
  Reporter,
} from '../types';

export const start =
  (storage: PerformanceStorage) =>
  (key: string, extraData: ExtraData = {}, options?: LatencyOptions) => {
    try {
      storage.addPoint(key, '', 'start', extraData, options);
    } catch (error) {
      storage.clearPoints(key);
    }
  };

export const cancel = (storage: PerformanceStorage) => (key: string) => {
  storage.clearPoints(key);
};

export const addStep =
  (storage: PerformanceStorage) => (key: string, stepName: string) => {
    try {
      storage.addPoint(key, stepName, 'step');
    } catch (error) {
      storage.clearPoints(key);
    }
  };

export const finishAndReport =
  (storage: PerformanceStorage) =>
  (reporter: Reporter) =>
  (
    key: string,
    extraData: ExtraData = {},
    options: {shouldReport?: (currentExtraData: ExtraData) => boolean} = {}
  ) => {
    let dataToReport: ExtraData | null = null;

    try {
      storage.addPoint(key, '', 'end', extraData);
    } catch (error) {
      storage.clearPoints(key);
      return;
    }

    try {
      dataToReport = storage.fetchEventData(key);
    } catch (error) {
      storage.clearPoints(key);
      return;
    }

    if (dataToReport) {
      try {
        const shouldReport = options.shouldReport
          ? options.shouldReport(dataToReport)
          : true;

        if (shouldReport) {
          reporter.report(key, dataToReport);
        }
      } catch (error) {
        console.error('[Latency Measurement] Failed to report:', error);
      }
    }

    storage.clearPoints(key);
  };
